import {
  EmailEventType,
  IEventsResponse,
  ICampaignAnalytics,
  CampaignStatsResponse,
  SendCampaignEmailRequest,
  SendCampaignEmailResponse,
} from '@/types/entities';

import { ApiService } from './api.service';
import { endpoints } from './endpoints';

/**
 * Campaign document from API
 */
export interface ICampaignDoc {
  _id: string;
  campaignName: string;
  subject: string;
  htmlBody: string;
  fromName?: string;
  marketingList?: {
    _id: string;
    name: string;
  };
  status: 'draft' | 'scheduled' | 'sending' | 'sent' | 'failed' | 'cancelled';
  totalRecipients: number;
  successCount: number;
  failedCount: number;
  openedCount: number;
  openRate: number;
  scheduledAt?: string;
  sentAt?: string;
  processingTime?: number;
  createdAt: string;
  updatedAt: string;
}

interface CampaignListResponse {
  data: ICampaignDoc[];
  meta: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

interface CampaignRecipient {
  contact?: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    position?: string;
    organization?: { _id: string; name: string };
  };
  email: string;
  status: 'sent' | 'failed';
  opened: boolean;
  openCount: number;
  sentAt?: string;
}

interface CampaignOpen {
  contact?: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    position?: string;
    organization?: { _id: string; name: string };
  };
  email: string;
  openedAt?: string;
  openCount: number;
}

interface PaginatedMetaResponse<T> {
  data: T[];
  meta: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

export interface CreateCampaignPayload {
  marketingListId?: string;
  campaignName: string;
  subject: string;
  htmlBody: string;
  fromName?: string;
  additionalContacts?: string[];
  status?: 'draft' | 'scheduled' | 'sent';
  scheduledAt?: Date | null;
}

/**
 * Service for handling marketing email campaign API calls
 */
export class MarketingCampaignApiService extends ApiService {
  /**
   * Creates a new campaign (draft or scheduled)
   */
  createCampaign = async (data: CreateCampaignPayload): Promise<ICampaignDoc> =>
    this.post(endpoints.marketingCampaigns.create, data);

  /**
   * Sends a marketing campaign to all contacts in a list
   */
  sendCampaign = async (data: SendCampaignEmailRequest): Promise<SendCampaignEmailResponse> =>
    this.post(endpoints.marketingCampaigns.send, data);

  /**
   * Gets statistics for a specific campaign
   */
  getCampaignStats = async (campaignName: string): Promise<CampaignStatsResponse> =>
    this.get(endpoints.marketingCampaigns.stats(campaignName));

  /**
   * Gets all campaigns with pagination
   */
  getCampaigns = async (page: number = 1, pageSize: number = 20): Promise<CampaignListResponse> =>
    this.get(`${endpoints.marketingCampaigns.all}?page=${page}&pageSize=${pageSize}`);

  /**
   * Gets a single campaign by ID
   */
  getCampaignById = async (id: string): Promise<ICampaignDoc> =>
    this.get(endpoints.marketingCampaigns.byId(id));

  /**
   * Deletes a campaign
   */
  deleteCampaign = async (id: string): Promise<void> =>
    this.delete(endpoints.marketingCampaigns.delete(id));

  /**
   * Gets all recipients of a campaign with their delivery status
   */
  getCampaignRecipients = async (
    id: string,
    page: number = 1,
    pageSize: number = 50,
  ): Promise<PaginatedMetaResponse<CampaignRecipient>> =>
    this.get(`${endpoints.marketingCampaigns.byId(id)}/recipients?page=${page}&pageSize=${pageSize}`);

  /**
   * Gets all contacts who opened the campaign email
   */
  getCampaignOpens = async (
    id: string,
    page: number = 1,
    pageSize: number = 50,
  ): Promise<PaginatedMetaResponse<CampaignOpen>> =>
    this.get(`${endpoints.marketingCampaigns.byId(id)}/opens?page=${page}&pageSize=${pageSize}`);

  /**
   * Gets analytics for a campaign
   */
  getCampaignAnalytics = async (id: string): Promise<ICampaignAnalytics> =>
    this.get(`${endpoints.marketingCampaigns.byId(id)}/analytics`);

  /**
   * Gets events for a campaign
   */
  getCampaignEvents = async (
    id: string,
    eventType?: EmailEventType,
    page: number = 1,
    pageSize: number = 50,
  ): Promise<IEventsResponse> => {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    });

    if (eventType) {
      params.append('eventType', eventType);
    }

    return this.get(`${endpoints.marketingCampaigns.byId(id)}/events?${params.toString()}`);
  };

  /**
   * Recalculates analytics for a campaign
   */
  recalculateAnalytics = async (id: string): Promise<ICampaignAnalytics> =>
    this.post(`${endpoints.marketingCampaigns.byId(id)}/analytics/recalculate`, {});
}

// Export a singleton instance
export const marketingCampaignApiService = new MarketingCampaignApiService();

// Named exports for convenience
export const { 
  createCampaign,
  sendCampaign, 
  getCampaignStats, 
  getCampaigns, 
  getCampaignById, 
  deleteCampaign,
  getCampaignRecipients,
  getCampaignOpens,
  getCampaignAnalytics,
  getCampaignEvents,
  recalculateAnalytics,
} = marketingCampaignApiService;
